import {
  apiService,
  authenticatedFetch,
  authenticatedStream,
} from "../../shared/services/apiService";

/*
  Ask Cadenza — Maestro /v1/assistant client.

  Chat turns stream back as Server-Sent Events over a POST, so they go
  through authenticatedStream and are parsed here; everything else is plain
  JSON through apiService. Every per-project call carries x-project-id —
  Maestro authorizes the assistant against project.read on that project.
*/

const projectHeaders = (projectId) => ({ "x-project-id": projectId });

/**
 * Parse a text/event-stream body, calling onEvent(name, data) per event.
 * Resolves when the stream ends; comment lines (": ping") are skipped.
 */
export const readEventStream = async (response, onEvent) => {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const flush = (chunk) => {
    let event = "message";
    const dataLines = [];
    for (const line of chunk.split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).trimStart());
    }
    if (!dataLines.length) return;
    let data;
    try {
      data = JSON.parse(dataLines.join("\n"));
    } catch {
      return;
    }
    onEvent(event, data);
  };
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
    let idx;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      flush(buffer.slice(0, idx));
      buffer = buffer.slice(idx + 2);
    }
  }
  if (buffer.trim()) flush(buffer);
};

export const assistantService = {
  getStatus: (projectId, token) =>
    apiService.get(
      `/v1/assistant/status?projectId=${encodeURIComponent(projectId)}`,
      token,
      projectHeaders(projectId)
    ),

  /** Ask a question; streams events into onEvent until the turn ends. */
  ask: async ({ projectId, scenarioId, conversationId, message, units, view }, token, onEvent, signal) => {
    const response = await authenticatedStream(
      "/v1/assistant/chat",
      {
        method: "POST",
        headers: projectHeaders(projectId),
        body: JSON.stringify({
          projectId,
          message,
          ...(conversationId ? { conversationId } : {}),
          ...(scenarioId ? { scenarioId } : {}),
          ...(units ? { units } : {}),
          ...(view ? { view } : {}),
        }),
        signal,
      },
      token
    );
    await readEventStream(response, onEvent);
  },

  /** Resume a turn the server handed back as "continuing". */
  continue: async (conversationId, projectId, token, onEvent, signal) => {
    const response = await authenticatedStream(
      `/v1/assistant/conversations/${conversationId}/continue`,
      { method: "POST", headers: projectHeaders(projectId), signal },
      token
    );
    await readEventStream(response, onEvent);
  },

  listConversations: (projectId, token, search) =>
    apiService.get(
      `/v1/assistant/conversations?projectId=${encodeURIComponent(projectId)}` +
        (search ? `&q=${encodeURIComponent(search)}` : ""),
      token,
      projectHeaders(projectId)
    ),

  getConversation: (conversationId, projectId, token) =>
    apiService.get(
      `/v1/assistant/conversations/${conversationId}`,
      token,
      projectHeaders(projectId)
    ),

  deleteConversation: (conversationId, projectId, token) =>
    authenticatedFetch(
      `/v1/assistant/conversations/${conversationId}`,
      { method: "DELETE", headers: projectHeaders(projectId) },
      token
    ).catch((err) => {
      // 204 has no JSON body; authenticatedFetch's parse fails after success.
      if (err instanceof SyntaxError) return null;
      throw err;
    }),

  // ── Company settings (company.write) ───────────────────────────────────────
  getSettings: (companyId, token) =>
    apiService.get(`/v1/assistant/companies/${companyId}/settings`, token),

  getUsage: (companyId, month, token) =>
    apiService.get(
      `/v1/assistant/companies/${companyId}/usage` +
        (month ? `?month=${encodeURIComponent(month)}` : ""),
      token
    ),

  updateSettings: (companyId, changes, token) =>
    apiService.put(`/v1/assistant/companies/${companyId}/settings`, changes, token),

  setKey: (companyId, apiKey, token) =>
    apiService.put(`/v1/assistant/companies/${companyId}/key`, { apiKey }, token),

  removeKey: (companyId, token) =>
    apiService.delete(`/v1/assistant/companies/${companyId}/key`, token),
};

/**
 * The user's display units, flattened to the short list the assistant uses
 * when quoting figures (Maestro sanitises keys and values again).
 */
export const unitsForAssistant = (prefs) => {
  if (!prefs) return undefined;
  const pick = {
    mass: prefs.physical?.mass,
    volume: prefs.physical?.volume,
    length: prefs.physical?.length,
    temperature: prefs.physical?.temperature,
    metal: prefs.metalRecovery?.massRecovery,
    grade: prefs.metalRecovery?.gradeOre?.combined,
    concentration: prefs.metalRecovery?.concentration?.combined,
    applicationRate: prefs.leaching?.applicationRate?.combined,
    flowRate: prefs.leaching?.flowRate?.combined,
    metalProduction: prefs.operations?.metalProduction,
  };
  return Object.fromEntries(
    Object.entries(pick).filter(([, v]) => typeof v === "string" && v)
  );
};
